<template>
  <div class="animated fadeIn" v-permission="PERMISSIONS.VIEW_PORTFOLIO">
    <b-card :no-body="true" footer-class="px-3 py-2 card-footer-action">
      <b-card-body class="p-3 clearfix">
        <b-row>
          <b-col cols="8">
            <i class="fa fa-shield bg-primary p-3 font-2xl mr-3 float-left"></i>
            <div class="h5 mb-0 mt-2">{{ vulnLabel }}</div>
            <div class="text-muted font-weight-bold font-xs">
              {{ sourceLabel }}
            </div>
            <div class="text-muted font-weight-bold font-xs">
              {{ vulnerability.title }}
            </div>
          </b-col>
          <b-col cols="4" class="align-middle" style="align-self: center">
            <b-row class="float-right align-middle">
              <div style="height: 40px">
                <div
                  class="text-center pull-left severity-bug-icon"
                  v-bind:style="{ background: severityColor }"
                >
                  <i
                    class="fa fa-bug"
                    style="font-size: 20px; padding: 10px"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="text-center pull-left severity-bug-label">
                  <div style="font-size: 20px; padding: 6px">
                    {{ severityLabel }}
                  </div>
                </div>
              </div>
            </b-row>
          </b-col>
        </b-row>
      </b-card-body>
      <div id="vulnerability-info-footer" slot="footer">
        <b-link
          class="font-weight-bold font-xs btn-block text-muted"
          v-b-modal.vulnerabilityDetailsModal
          >{{ $t('message.view_details') }}
          <i class="fa fa-angle-right float-right font-lg"></i
        ></b-link>
      </div>
    </b-card>

    <div v-if="vulnerability.published || vulnerability.aliases">
      <b-card>
        <b-card-text>
          <span v-if="vulnerability.published" class="font-weight-bold font-xs"
            >Published:
          </span>
          <span
            v-if="vulnerability.published"
            class="font-weight-bold font-xs"
            style="margin-right: 1.2rem"
            >{{ prettyTimestamp }}</span
          >
          <span
            v-if="vulnerability.aliases && vulnerability.aliases.length > 0"
            class="font-weight-bold font-xs"
          >
            Aliases:
            <span v-for="alias in resolveVulnAliases(vulnerability.aliases)">
              <b-link
                style="margin-right: 1rem"
                :href="`/vulnerabilities/${alias.source}/${encodeURIComponent(alias.vulnId)}`"
                >{{ alias.vulnId }}</b-link
              >
            </span>
          </span>
        </b-card-text>
      </b-card>
    </div>

    <b-tabs
      class="body-bg-color"
      style="border-left: 0; border-right: 0; border-top: 0"
    >
      <b-tab
        ref="overview"
        class="body-bg-color overview-chart"
        style="border-left: 0; border-right: 0; border-top: 0"
        active
        @click="routeTo()"
      >
        <template v-slot:title
          ><i class="fa fa-line-chart"></i>
          {{ $t('message.overview') }}</template
        >

        <div v-if="vulnerability.description">
          <b-card :title="this.$t('message.overview')">
            <b-card-text>
              <showdown :markdown="vulnerability.description" flavor="github" />
            </b-card-text>
            <div v-if="cvssBaseScore > 0" slot="footer">
              <b-row class="text-center">
                <b-col class="mb-sm-2 mb-0">
                  <div class="text-muted">
                    {{ $t('message.cvss_base_score') }}
                  </div>
                  <strong>{{ cvssBaseScore }}</strong>
                  <b-progress
                    height="{}"
                    class="progress-xs mt-2"
                    :precision="1"
                    :variant="vulnerability.severity"
                    v-bind:value="(cvssBaseScore / 10) * 100"
                  ></b-progress>
                </b-col>
                <b-col class="mb-sm-2 mb-0 d-sm-down-none">
                  <div class="text-muted">
                    {{ $t('message.cvss_impact_subscore') }}
                  </div>
                  <strong>{{ cvssImpactScore }}</strong>
                  <b-progress
                    height="{}"
                    class="progress-xs mt-2"
                    :precision="1"
                    variant="severity-info"
                    v-bind:value="(cvssImpactScore / 10) * 100"
                  ></b-progress>
                </b-col>
                <b-col class="mb-sm-2 mb-0 d-sm-down-none">
                  <div class="text-muted">
                    {{ $t('message.cvss_exploitability_subscore') }}
                  </div>
                  <strong>{{ cvssExploitScore }}</strong>
                  <b-progress
                    height="{}"
                    class="progress-xs mt-2"
                    :precision="1"
                    variant="severity-info"
                    v-bind:value="(cvssExploitScore / 10) * 100"
                  ></b-progress>
                </b-col>
                <b-col class="mb-sm-2 mb-0 d-sm-down-none">
                  <div class="text-muted">
                    {{ $t('message.epss_score') }}
                  </div>
                  <strong>{{ epssScore }}</strong>
                  <b-progress
                    height="{}"
                    class="progress-xs mt-2"
                    :precision="1"
                    variant="severity-info"
                    v-bind:value="epssScore * 100"
                  ></b-progress>
                </b-col>
                <b-col class="mb-sm-2 mb-0 d-sm-down-none">
                  <div class="text-muted">
                    {{ $t('message.epss_percentile') }}
                  </div>
                  <strong>{{ epssPercentile }}</strong>
                  <b-progress
                    height="{}"
                    class="progress-xs mt-2"
                    :precision="1"
                    variant="severity-info"
                    v-bind:value="epssPercentile * 100"
                  ></b-progress>
                </b-col>
              </b-row>
            </div>
            <div v-if="owaspRRLikelihoodScore > 0" slot="footer">
              <b-row class="text-center">
                <b-col class="mb-sm-2 mb-0">
                  <div class="text-muted">
                    {{ $t('message.owasp_rr_likelihood_score') }}
                  </div>
                  <strong>{{ owaspRRLikelihoodScore }}</strong>
                  <b-progress
                    height="{}"
                    class="progress-xs mt-2"
                    :precision="1"
                    variant="severity-info"
                    v-bind:value="(owaspRRLikelihoodScore / 10) * 100"
                  ></b-progress>
                </b-col>
                <b-col class="mb-sm-2 mb-0 d-sm-down-none">
                  <div class="text-muted">
                    {{ $t('message.owasp_rr_technical_impact_score') }}
                  </div>
                  <strong>{{ owaspRRTechnicalImpactScore }}</strong>
                  <b-progress
                    height="{}"
                    class="progress-xs mt-2"
                    :precision="1"
                    variant="severity-info"
                    v-bind:value="(owaspRRTechnicalImpactScore / 10) * 100"
                  ></b-progress>
                </b-col>
                <b-col class="mb-sm-2 mb-0 d-sm-down-none">
                  <div class="text-muted">
                    {{ $t('message.owasp_rr_business_impact_score') }}
                  </div>
                  <strong>{{ owaspRRBusinessImpactScore }}</strong>
                  <b-progress
                    height="{}"
                    class="progress-xs mt-2"
                    :precision="1"
                    variant="severity-info"
                    v-bind:value="(owaspRRBusinessImpactScore / 10) * 100"
                  ></b-progress>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </div>

        <div v-if="vulnerability.cwes">
          <b-card :title="this.$t('message.weakness')">
            <b-card-text v-for="cwe in vulnerability.cwes" :key="cwe.cweId">
              <a :href="cweLink(cwe)">{{ cweLabel(cwe) }}</a>
            </b-card-text>
          </b-card>
        </div>

        <div v-if="vulnerability.detail">
          <b-card :title="this.$t('message.details')">
            <b-card-text>
              <showdown :markdown="vulnerability.detail" />
            </b-card-text>
          </b-card>
        </div>

        <div v-if="vulnerability.recommendation">
          <b-card :title="this.$t('message.recommendation')">
            <b-card-text>
              <showdown :markdown="vulnerability.recommendation" />
            </b-card-text>
          </b-card>
        </div>

        <div v-if="vulnerability.references">
          <b-card :title="this.$t('message.references')">
            <b-card-text>
              <showdown :markdown="vulnerability.references" />
            </b-card-text>
          </b-card>
        </div>

        <div v-if="vulnerability.credits">
          <b-card :title="this.$t('message.credits')">
            <b-card-text>
              <showdown :markdown="vulnerability.credits" />
            </b-card-text>
          </b-card>
        </div>
      </b-tab>
      <b-tab
        ref="affectedprojects"
        v-if="isPermitted(PERMISSIONS.VIEW_VULNERABILITY)"
        @click="routeTo('affectedProjects')"
      >
        <template v-slot:title
          ><i class="fa fa-tasks"></i> {{ $t('message.affected_projects') }}
          <b-badge variant="tab-total">{{
            totalAffectedProjects
          }}</b-badge></template
        >
        <affected-projects
          v-if="this.vulnerability.uuid"
          :key="this.uuid"
          :vulnerability="vulnerability.uuid"
          :source="source"
          :vulnId="vulnId"
          v-on:total="totalAffectedProjects = $event"
        />
      </b-tab>
    </b-tabs>
    <vulnerability-details-modal
      :vulnProp="clonedVulnerability"
      v-on:vulnerabilityUpdated="syncVulnerabilityFields"
    />
  </div>
</template>

<script>
import common from '../../../shared/common';
import { getStyle } from '@coreui/coreui/dist/js/coreui-utilities';
import PortfolioWidgetRow from '../../dashboard/PortfolioWidgetRow';
import SeverityBarChart from '../../dashboard/SeverityBarChart';
import EventBus from '../../../shared/eventbus';
import permissionsMixin from '../../../mixins/permissionsMixin';
import AffectedProjects from './AffectedProjects';
import VulnerabilityDetailsModal from './VulnerabilityDetailsModal';
import Showdown from '@/views/components/Showdown';
import { cloneDeep } from 'lodash-es';

export default {
  mixins: [permissionsMixin],
  components: {
    SeverityBarChart,
    PortfolioWidgetRow,
    AffectedProjects,
    VulnerabilityDetailsModal,
    Showdown,
  },
  title: '',
  computed: {
    vulnLabel() {
      return `${this.vulnId} (${this.source})`;
    },
    cvssBaseScore() {
      return common.valueWithDefault(
        this.vulnerability.cvssV3BaseScore
          ? this.vulnerability.cvssV3BaseScore
          : this.vulnerability.cvssV2BaseScore,
        0,
      );
    },
    cvssImpactScore() {
      return common.valueWithDefault(
        this.vulnerability.cvssV3ImpactSubScore
          ? this.vulnerability.cvssV3ImpactSubScore
          : this.vulnerability.cvssV2ImpactSubScore,
        0,
      );
    },
    cvssExploitScore() {
      return common.valueWithDefault(
        this.vulnerability.cvssV3ExploitabilitySubScore
          ? this.vulnerability.cvssV3ExploitabilitySubScore
          : this.vulnerability.cvssV2ExploitabilitySubScore,
        0,
      );
    },
    epssScore() {
      return common.valueWithDefault(this.vulnerability.epssScore, 0);
    },
    epssPercentile() {
      return common.valueWithDefault(this.vulnerability.epssPercentile, 0);
    },
    owaspRRLikelihoodScore() {
      return common.valueWithDefault(
        this.vulnerability.owaspRRLikelihoodScore,
        0,
      );
    },
    owaspRRTechnicalImpactScore() {
      return common.valueWithDefault(
        this.vulnerability.owaspRRTechnicalImpactScore,
        0,
      );
    },
    owaspRRBusinessImpactScore() {
      return common.valueWithDefault(
        this.vulnerability.owaspRRBusinessImpactScore,
        0,
      );
    },
    prettyTimestamp() {
      return common.formatTimestamp(this.vulnerability.published);
    },
    sourceLabel() {
      switch (this.vulnerability.source) {
        case 'NVD':
          return 'National Vulnerability Database';
        case 'NPM':
          return 'NPM Advisories';
        case 'OSSINDEX':
          return 'Sonatype OSS Index';
        case 'VULNDB':
          return 'VulnDB (Risk Based Security)';
        case 'SNYK':
          return 'Snyk';
        case 'TRIVY':
          return 'Trivy';
        default:
          return '';
      }
    },
    severityLabel() {
      switch (this.vulnerability.severity) {
        case 'CRITICAL':
          return this.$t('severity.critical_severity');
        case 'HIGH':
          return this.$t('severity.high_severity');
        case 'MEDIUM':
          return this.$t('severity.medium_severity');
        case 'LOW':
          return this.$t('severity.low_severity');
        case 'INFO':
          return this.$t('severity.info_severity');
        default:
          return this.$t('severity.unassigned_severity');
      }
    },
    severityColor() {
      switch (this.vulnerability.severity) {
        case 'CRITICAL':
          return this.severityCritical;
        case 'HIGH':
          return this.severityHigh;
        case 'MEDIUM':
          return this.severityMedium;
        case 'LOW':
          return this.severityLow;
        case 'INFO':
          return this.severityInfo;
        default:
          return this.severityUnassigned;
      }
    },
  },
  data() {
    return {
      severityCritical: this.getStyle('--severity-critical'),
      severityHigh: this.getStyle('--severity-high'),
      severityMedium: this.getStyle('--severity-medium'),
      severityLow: this.getStyle('--severity-low'),
      severityUnassigned: this.getStyle('--severity-unassigned'),
      severityInfo: this.getStyle('--severity-info'),
      trackColor: this.getStyle('--component-active-color'),
      primaryColor: this.getStyle('--primary'),
      uuid: null,
      source: null,
      vulnId: null,
      vulnerability: {},
      clonedVulnerability: {},
      totalAffectedProjects: 0,
    };
  },
  methods: {
    getStyle: function (style) {
      return getStyle(style);
    },
    syncVulnerabilityFields: function (vulnerability) {
      this.vulnerability = vulnerability;
    },
    cweLabel: function (cwe) {
      return `CWE-${cwe.cweId}: ${cwe.name}`;
    },
    cweLink: function (cwe) {
      return `https://cwe.mitre.org/data/definitions/${cwe.cweId}`;
    },
    resolveVulnAliases: function (aliases) {
      return common.resolveVulnAliases(this.source, aliases);
    },
    loadData: function () {
      let url = '';
      if (this.uuid) {
        url = `${this.$api.BASE_URL}/${this.$api.URL_VULNERABILITY}/${this.uuid}`;
      } else {
        url = `${this.$api.BASE_URL}/${this.$api.URL_VULNERABILITY}/source/${this.source}/vuln/${encodeURIComponent(this.vulnId)}`;
      }
      this.axios.get(url).then((response) => {
        this.vulnerability = response.data;
        if (!this.source) {
          this.source = response.data.source;
        }
        if (!this.vulnerability.affectedComponents) {
          this.vulnerability.affectedComponents = []; // Initialize array so that components may be added to it.
        }
        if (!this.vulnId) {
          this.vulnId = response.data.vulnId;
        }
        EventBus.$emit('addCrumb', this.vulnLabel);
        this.$title = `${this.$t('message.vulnerability')} ${this.vulnLabel}`;
        this.clonedVulnerability = cloneDeep(this.vulnerability);
      });
    },
    initializeData: function () {
      this.uuid = this.$route.params.uuid;
      this.source = this.$route.params.source;
      this.vulnId = decodeURIComponent(this.$route.params.vulnId);
    },
    routeTo(path) {
      if (path) {
        if (
          !this.$route.fullPath.toLowerCase().includes('/' + path.toLowerCase())
        ) {
          this.$router.push({
            path:
              '/vulnerabilities/' +
              this.source +
              '/' +
              encodeURIComponent(this.vulnId) +
              '/' +
              path,
          });
        }
      } else if (
        this.$route.fullPath !==
          '/vulnerabilities/' +
            this.source +
            '/' +
            encodeURIComponent(this.vulnId) &&
        this.$route.fullPath !==
          '/vulnerabilities/' +
            this.source +
            '/' +
            encodeURIComponent(this.vulnId) +
            '/'
      ) {
        this.$router.push({
          path:
            '/vulnerabilities/' +
            this.source +
            '/' +
            encodeURIComponent(this.vulnId) +
            '/',
        });
      }
    },
    getTabFromRoute: function () {
      let pattern = new RegExp(
        '/vulnerabilities\\/' +
          this.source +
          '\\/' +
          encodeURIComponent(this.vulnId) +
          '\\/([^\\/]*)',
        'gi',
      );
      let tab = pattern.exec(this.$route.fullPath.toLowerCase());
      return this.$refs[tab && tab[1] ? tab[1].toLowerCase() : 'overview'];
    },
  },
  watch: {
    '$route.params.uuid'(newValue) {
      EventBus.$emit('crumble');
      this.initializeData();
      this.loadData();
    },
    '$route.params.vulnId'(newValue) {
      EventBus.$emit('crumble');
      this.initializeData();
      this.loadData();
    },
    $route() {
      this.getTabFromRoute().activate();
    },
  },
  beforeMount() {
    this.initializeData();
  },
  mounted() {
    this.loadData();
    try {
      this.getTabFromRoute().active = true;
    } catch (e) {
      this.$toastr.e(this.$t('condition.forbidden'));
      this.$router.replace({
        path:
          '/vulnerabilities/' +
          this.source +
          '/' +
          encodeURIComponent(this.vulnId) +
          '/',
      });
      this.$refs.overview.active = true;
    }
  },
  destroyed() {
    EventBus.$emit('crumble');
  },
};
</script>
